import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  TrendingUp,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Check,
  ArrowRight,
  Calculator,
  Hash,
  Edit3,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { formatCurrency, QUOTE_STATUSES } from "../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MASS_FIELD_OPTIONS = [
  { value: "status", label: "Status", type: "select", options: QUOTE_STATUSES },
  { value: "valid_until", label: "Valid until (expiry date)", type: "date" },
  { value: "frequency", label: "Frequency", type: "select", options: [
    { value: "one_time", label: "One-time" },
    { value: "weekly", label: "Weekly" },
    { value: "biweekly", label: "Bi-weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "yearly", label: "Yearly" },
  ]},
  { value: "quote_type", label: "Quote type", type: "select", options: [
    { value: "standard", label: "Standard" },
    { value: "maintenance", label: "Maintenance" },
    { value: "project", label: "Project" },
    { value: "service", label: "Service" },
  ]},
  { value: "priority", label: "Priority", type: "select", options: [
    { value: "low", label: "Low" },
    { value: "normal", label: "Normal" },
    { value: "high", label: "High" },
    { value: "urgent", label: "Urgent" },
  ]},
  { value: "estimated_duration", label: "Estimated duration / job length", type: "text" },
  { value: "property_address", label: "Property / job address", type: "text" },
  { value: "description", label: "Description", type: "text" },
  { value: "notes", label: "Notes (client-visible)", type: "text" },
  { value: "internal_notes", label: "Internal notes (admin only)", type: "text" },
  { value: "tax_rate", label: "Tax rate (%)", type: "number" },
  { value: "piecework_hours", label: "Piecework hours", type: "number" },
];

const MassQuoteUpdate = () => {
  const { isOfficeManager } = useAuth();
  const [activeTab, setActiveTab] = useState("update");

  // ===== Mass Update (% increase) =====
  const [loading, setLoading] = useState(false);
  const [percentageIncrease, setPercentageIncrease] = useState(10);
  const [updateDateToToday, setUpdateDateToToday] = useState(false);
  const [statusFilter, setStatusFilter] = useState([]);
  const [preview, setPreview] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [applying, setApplying] = useState(false);

  // ===== Renumbering =====
  const [rnMode, setRnMode] = useState("continue_from_last");
  const [rnPrefix, setRnPrefix] = useState("Q-");
  const [rnPad, setRnPad] = useState(4);
  const [rnStart, setRnStart] = useState(1);
  const [rnStatusFilter, setRnStatusFilter] = useState([]);
  const [rnPreserve, setRnPreserve] = useState(false);
  const [rnPreview, setRnPreview] = useState(null);
  const [rnLoading, setRnLoading] = useState(false);
  const [rnApplying, setRnApplying] = useState(false);

  // ===== Field updates =====
  const [fuFields, setFuFields] = useState([]); // [{ field, value }]
  const [fuStatusFilter, setFuStatusFilter] = useState([]);
  const [fuPreview, setFuPreview] = useState(null);
  const [fuLoading, setFuLoading] = useState(false);
  const [fuApplying, setFuApplying] = useState(false);

  // ===== Discrepancies =====
  const [discrepancies, setDiscrepancies] = useState([]);
  const [thresholdPercent, setThresholdPercent] = useState(10);
  const [loadingDiscrepancies, setLoadingDiscrepancies] = useState(false);

  useEffect(() => {
    if (activeTab === "discrepancies") fetchDiscrepancies();
  }, [activeTab, thresholdPercent]);

  const fetchDiscrepancies = async () => {
    setLoadingDiscrepancies(true);
    try {
      const response = await axios.get(`${API}/quotes/discrepancies?threshold_percent=${thresholdPercent}`);
      setDiscrepancies(response.data.discrepancies || []);
    } catch {
      toast.error("Failed to fetch discrepancies");
    } finally {
      setLoadingDiscrepancies(false);
    }
  };

  // ===== % increase handlers =====
  const handlePreview = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/quotes/mass-update/preview`, {
        percentage_increase: percentageIncrease,
        update_date_to_today: updateDateToToday,
        status_filter: statusFilter.length > 0 ? statusFilter : null,
      });
      setPreview(response.data);
    } catch {
      toast.error("Failed to generate preview");
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      const response = await axios.post(`${API}/quotes/mass-update/apply`, {
        percentage_increase: percentageIncrease,
        update_date_to_today: updateDateToToday,
        status_filter: statusFilter.length > 0 ? statusFilter : null,
      });
      toast.success(`Updated ${response.data.updated_count} quotes`);
      setShowConfirmDialog(false);
      setPreview(null);
    } catch {
      toast.error("Failed to apply updates");
    } finally {
      setApplying(false);
    }
  };

  const toggleStatus = (setter, current, status) => {
    setter(current.includes(status) ? current.filter((s) => s !== status) : [...current, status]);
  };

  // ===== Renumber handlers =====
  const buildRnPayload = () => ({
    mode: rnMode,
    prefix: rnPrefix,
    pad_width: Number(rnPad) || 0,
    start_number: Number(rnStart) || 1,
    status_filter: rnStatusFilter.length > 0 ? rnStatusFilter : null,
    preserve_existing: rnPreserve,
  });

  const handleRnPreview = async () => {
    setRnLoading(true);
    try {
      const r = await axios.post(`${API}/quotes/mass-renumber/preview`, buildRnPayload());
      setRnPreview(r.data);
      toast.success(`${r.data.total} quotes ready to renumber (starting at ${r.data.start_number})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to preview renumber");
    } finally {
      setRnLoading(false);
    }
  };

  const handleRnApply = async () => {
    setRnApplying(true);
    try {
      const r = await axios.post(`${API}/quotes/mass-renumber/apply`, buildRnPayload());
      toast.success(`Renumbered ${r.data.renumbered_count} quotes`);
      setRnPreview(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to renumber");
    } finally {
      setRnApplying(false);
    }
  };

  // ===== Field-update handlers =====
  const updatesObj = () => {
    const out = {};
    for (const f of fuFields) {
      if (!f.field) continue;
      out[f.field] = f.value ?? "";
    }
    return out;
  };

  const handleFuPreview = async () => {
    if (fuFields.length === 0) { toast.error("Add at least one field to update"); return; }
    setFuLoading(true);
    try {
      const r = await axios.post(`${API}/quotes/mass-field-update/preview`, {
        updates: updatesObj(),
        status_filter: fuStatusFilter.length > 0 ? fuStatusFilter : null,
      });
      setFuPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to preview field update");
    } finally {
      setFuLoading(false);
    }
  };

  const handleFuApply = async () => {
    setFuApplying(true);
    try {
      const r = await axios.post(`${API}/quotes/mass-field-update/apply`, {
        updates: updatesObj(),
        status_filter: fuStatusFilter.length > 0 ? fuStatusFilter : null,
      });
      toast.success(`Updated ${r.data.updated_count} quotes (${r.data.fields.join(", ")})`);
      setFuPreview(null);
      setFuFields([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to apply field update");
    } finally {
      setFuApplying(false);
    }
  };

  if (!isOfficeManager) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Only Office Managers can access this feature</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="mass-update-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Mass Quote Management</h1>
        <p className="text-muted-foreground mt-1">
          Bulk update pricing, renumber, change fields, and review hour discrepancies.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-3xl grid-cols-4">
          <TabsTrigger value="update" className="flex items-center gap-2" data-testid="tab-mass-percent-update">
            <TrendingUp className="h-4 w-4" /> % Increase
          </TabsTrigger>
          <TabsTrigger value="renumber" className="flex items-center gap-2" data-testid="tab-mass-renumber">
            <Hash className="h-4 w-4" /> Renumber
          </TabsTrigger>
          <TabsTrigger value="fields" className="flex items-center gap-2" data-testid="tab-mass-field-update">
            <Edit3 className="h-4 w-4" /> Field Updates
          </TabsTrigger>
          <TabsTrigger value="discrepancies" className="flex items-center gap-2" data-testid="tab-mass-discrepancies">
            <AlertTriangle className="h-4 w-4" /> Discrepancies
          </TabsTrigger>
        </TabsList>

        {/* ===== % INCREASE TAB ===== */}
        <TabsContent value="update" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Update Quote Pricing</CardTitle>
              <CardDescription>Apply percentage increases to all or selected quotes (e.g., year-end price adjustments)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Percentage Increase (%)</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={percentageIncrease}
                      onChange={(e) => setPercentageIncrease(parseFloat(e.target.value) || 0)}
                      className="w-32"
                      data-testid="percentage-input"
                    />
                    <span className="text-muted-foreground">%</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Negative value to decrease prices.</p>
                </div>
                <div className="space-y-2">
                  <Label>Options</Label>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="update-date" checked={updateDateToToday} onCheckedChange={setUpdateDateToToday} />
                    <Label htmlFor="update-date" className="text-sm font-normal">Update quote dates to today (renews expired quotes)</Label>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Filter by Status (optional)</Label>
                <div className="flex flex-wrap gap-2">
                  {QUOTE_STATUSES.map((s) => (
                    <Button key={s.value} type="button"
                      variant={statusFilter.includes(s.value) ? "default" : "outline"} size="sm"
                      onClick={() => toggleStatus(setStatusFilter, statusFilter, s.value)}>
                      {s.label}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={handlePreview} disabled={loading} data-testid="preview-btn">
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  <Calculator className="h-4 w-4 mr-2" /> Preview Changes
                </Button>
                {preview && <Button variant="outline" onClick={() => setPreview(null)}>Clear</Button>}
              </div>
            </CardContent>
          </Card>

          {preview && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Preview Results</CardTitle>
                  <CardDescription>{preview.total_quotes} quotes will be updated</CardDescription>
                </div>
                <Button onClick={() => setShowConfirmDialog(true)} data-testid="apply-btn">
                  Apply Changes <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className="max-h-96 overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Quote #</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead className="text-right">Old Total</TableHead>
                        <TableHead className="text-right">New Total</TableHead>
                        <TableHead className="text-right">Diff</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.quotes.slice(0, 50).map((q) => (
                        <TableRow key={q.id}>
                          <TableCell className="font-mono">{q.quote_number}</TableCell>
                          <TableCell className="max-w-xs truncate">{q.client_name}</TableCell>
                          <TableCell className="text-right">{formatCurrency(q.old_total)}</TableCell>
                          <TableCell className="text-right font-semibold">{formatCurrency(q.new_total)}</TableCell>
                          <TableCell className="text-right text-emerald-600">+{formatCurrency(q.diff)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {preview.quotes.length > 50 && (
                    <p className="text-center text-sm text-muted-foreground mt-4">Showing 50 of {preview.quotes.length} quotes</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ===== RENUMBER TAB ===== */}
        <TabsContent value="renumber" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Mass-Renumber Quotes</CardTitle>
              <CardDescription>
                Re-number quotes based on how you organise them. Useful when starting a new year, migrating from another tool, or cleaning up legacy numbers.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Numbering mode</Label>
                  <Select value={rnMode} onValueChange={setRnMode}>
                    <SelectTrigger data-testid="renumber-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="continue_from_last">Continue from your last quote number</SelectItem>
                      <SelectItem value="start_at_one">Start at 1</SelectItem>
                      <SelectItem value="custom">Custom start number</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {rnMode === "continue_from_last" && "Finds the highest trailing number across your existing quotes and continues from there."}
                    {rnMode === "start_at_one" && "Restart numbering at 1 (use carefully – existing numbers will change)."}
                    {rnMode === "custom" && "Pick exactly which number to start from."}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Prefix (text before the number)</Label>
                  <Input value={rnPrefix} onChange={(e) => setRnPrefix(e.target.value)} placeholder="e.g., Q-, INV-, BG-2026-"
                    data-testid="renumber-prefix-input" />
                  <p className="text-xs text-muted-foreground">Leave blank for numbers only (e.g., 1, 2, 3...).</p>
                </div>
                <div className="space-y-2">
                  <Label>Zero-pad to width</Label>
                  <Input type="number" min={0} max={10} value={rnPad}
                    onChange={(e) => setRnPad(parseInt(e.target.value) || 0)}
                    data-testid="renumber-pad-input" />
                  <p className="text-xs text-muted-foreground">e.g., 4 → 0001, 0002. Use 0 for no padding.</p>
                </div>
                {rnMode === "custom" && (
                  <div className="space-y-2">
                    <Label>Start number</Label>
                    <Input type="number" value={rnStart}
                      onChange={(e) => setRnStart(parseInt(e.target.value) || 1)}
                      data-testid="renumber-start-input" />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Filter by status (optional)</Label>
                <div className="flex flex-wrap gap-2">
                  {QUOTE_STATUSES.map((s) => (
                    <Button key={s.value} type="button"
                      variant={rnStatusFilter.includes(s.value) ? "default" : "outline"} size="sm"
                      onClick={() => toggleStatus(setRnStatusFilter, rnStatusFilter, s.value)}>
                      {s.label}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox id="rn-preserve" checked={rnPreserve} onCheckedChange={setRnPreserve} />
                <Label htmlFor="rn-preserve" className="text-sm font-normal">
                  Skip quotes whose number already starts with this prefix (preserve current numbering when possible)
                </Label>
              </div>

              <div className="flex gap-2">
                <Button onClick={handleRnPreview} disabled={rnLoading} data-testid="renumber-preview-btn">
                  {rnLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  <Calculator className="h-4 w-4 mr-2" /> Preview Renumbering
                </Button>
                {rnPreview && (
                  <>
                    <Button onClick={handleRnApply} disabled={rnApplying} data-testid="renumber-apply-btn">
                      {rnApplying && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Apply Renumbering <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                    <Button variant="outline" onClick={() => setRnPreview(null)}>Clear</Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {rnPreview && (
            <Card>
              <CardHeader>
                <CardTitle>Renumbering Plan</CardTitle>
                <CardDescription>
                  Starting at {rnPreview.start_number}. {rnPreview.total} quotes will be renumbered.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="max-h-96 overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Current #</TableHead>
                        <TableHead></TableHead>
                        <TableHead>New #</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rnPreview.plan.slice(0, 100).map((p) => (
                        <TableRow key={p.id}>
                          <TableCell className="font-mono">{p.old}</TableCell>
                          <TableCell><ArrowRight className="h-4 w-4 text-muted-foreground" /></TableCell>
                          <TableCell className="font-mono font-semibold">{p.new}</TableCell>
                          <TableCell>
                            {p.skipped
                              ? <Badge variant="outline" className="bg-muted">Skipped</Badge>
                              : <Badge variant="default">Will renumber</Badge>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ===== FIELD UPDATES TAB ===== */}
        <TabsContent value="fields" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Mass Field Updates</CardTitle>
              <CardDescription>
                Update any combination of client info, job specs, length, frequency, expiry date, status, or notes across many quotes at once.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3" data-testid="field-updates-builder">
                {fuFields.map((row, idx) => {
                  const opt = MASS_FIELD_OPTIONS.find((o) => o.value === row.field);
                  return (
                    <div key={idx} className="grid grid-cols-[200px_1fr_auto] gap-2 items-center">
                      <Select
                        value={row.field || ""}
                        onValueChange={(v) => {
                          const next = [...fuFields];
                          next[idx] = { field: v, value: "" };
                          setFuFields(next);
                        }}
                      >
                        <SelectTrigger data-testid={`field-select-${idx}`}><SelectValue placeholder="Select field..." /></SelectTrigger>
                        <SelectContent>
                          {MASS_FIELD_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}
                              disabled={fuFields.some((f, i) => i !== idx && f.field === o.value)}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {opt?.type === "select" ? (
                        <Select value={row.value || ""} onValueChange={(v) => {
                          const next = [...fuFields]; next[idx] = { ...next[idx], value: v }; setFuFields(next);
                        }}>
                          <SelectTrigger data-testid={`field-value-${idx}`}><SelectValue placeholder="New value..." /></SelectTrigger>
                          <SelectContent>
                            {opt.options.map((op) => (
                              <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : opt?.type === "date" ? (
                        <Input type="date" value={row.value || ""} onChange={(e) => {
                          const next = [...fuFields]; next[idx] = { ...next[idx], value: e.target.value }; setFuFields(next);
                        }} data-testid={`field-value-${idx}`} />
                      ) : opt?.type === "number" ? (
                        <Input type="number" step="0.01" value={row.value || ""} onChange={(e) => {
                          const next = [...fuFields]; next[idx] = { ...next[idx], value: e.target.value }; setFuFields(next);
                        }} data-testid={`field-value-${idx}`} />
                      ) : (
                        <Input value={row.value || ""} onChange={(e) => {
                          const next = [...fuFields]; next[idx] = { ...next[idx], value: e.target.value }; setFuFields(next);
                        }} placeholder="New value" disabled={!row.field} data-testid={`field-value-${idx}`} />
                      )}
                      <Button variant="ghost" size="sm" onClick={() => setFuFields(fuFields.filter((_, i) => i !== idx))}
                        data-testid={`field-remove-${idx}`} className="text-destructive">
                        ✕
                      </Button>
                    </div>
                  );
                })}
                <Button variant="outline" size="sm" onClick={() => setFuFields([...fuFields, { field: "", value: "" }])}
                  data-testid="add-field-btn">
                  + Add field
                </Button>
              </div>

              <div className="space-y-2">
                <Label>Filter by status (optional)</Label>
                <div className="flex flex-wrap gap-2">
                  {QUOTE_STATUSES.map((s) => (
                    <Button key={s.value} type="button"
                      variant={fuStatusFilter.includes(s.value) ? "default" : "outline"} size="sm"
                      onClick={() => toggleStatus(setFuStatusFilter, fuStatusFilter, s.value)}>
                      {s.label}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={handleFuPreview} disabled={fuLoading || fuFields.length === 0} data-testid="field-preview-btn">
                  {fuLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  <Calculator className="h-4 w-4 mr-2" /> Preview Changes
                </Button>
                {fuPreview && (
                  <>
                    <Button onClick={handleFuApply} disabled={fuApplying} data-testid="field-apply-btn">
                      {fuApplying && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Apply to {fuPreview.total} quotes <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                    <Button variant="outline" onClick={() => setFuPreview(null)}>Clear</Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {fuPreview && (
            <Card>
              <CardHeader>
                <CardTitle>Field Update Preview</CardTitle>
                <CardDescription>{fuPreview.total} quotes will receive: {fuPreview.fields.join(", ")}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="max-h-96 overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Quote #</TableHead>
                        {fuPreview.fields.map((f) => (
                          <TableHead key={f}>{f}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {fuPreview.changes.slice(0, 50).map((c) => (
                        <TableRow key={c.id}>
                          <TableCell className="font-mono">{c.quote_number}</TableCell>
                          {fuPreview.fields.map((f) => (
                            <TableCell key={f} className="text-xs">
                              <span className="line-through text-muted-foreground">{String(c.diff[f]?.from ?? "—")}</span>
                              {" → "}
                              <span className="font-semibold">{String(c.diff[f]?.to ?? "—")}</span>
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ===== DISCREPANCIES TAB ===== */}
        <TabsContent value="discrepancies" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" /> Hour Discrepancies
              </CardTitle>
              <CardDescription>Quotes where the most-recent total moved sharply from the previous one for the same client.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Label>Threshold:</Label>
                  <Input type="number" value={thresholdPercent}
                    onChange={(e) => setThresholdPercent(parseFloat(e.target.value) || 10)} className="w-20" />
                  <span className="text-muted-foreground">%</span>
                </div>
                <Button variant="outline" size="sm" onClick={fetchDiscrepancies} disabled={loadingDiscrepancies}>
                  {loadingDiscrepancies ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                </Button>
              </div>

              {loadingDiscrepancies ? (
                <div className="flex items-center justify-center h-32"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
              ) : discrepancies.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Check className="h-12 w-12 mx-auto mb-4 text-emerald-500" />
                  <p>No discrepancies found above {thresholdPercent}% threshold</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Quote #</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead className="text-right">Previous Total</TableHead>
                      <TableHead className="text-right">Current Total</TableHead>
                      <TableHead className="text-right">% Diff</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {discrepancies.map((d) => (
                      <TableRow key={d.current_quote_id}>
                        <TableCell>
                          <a href={`/quotes/${d.current_quote_id}`} className="font-mono text-primary hover:underline">
                            {d.current_quote_number}
                          </a>
                        </TableCell>
                        <TableCell>{d.client_name}</TableCell>
                        <TableCell className="text-right">{formatCurrency(d.previous_total)}</TableCell>
                        <TableCell className="text-right font-semibold">{formatCurrency(d.current_total)}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant="outline"
                            className={Math.abs(d.percentage_diff) > 20
                              ? "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
                              : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"}>
                            {d.percentage_diff > 0 ? "+" : ""}{d.percentage_diff}%
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Confirm Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Mass Update</DialogTitle>
            <DialogDescription>
              This will update {preview?.total_quotes} quotes with a {percentageIncrease}% price change. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>Cancel</Button>
            <Button onClick={handleApply} disabled={applying} data-testid="confirm-apply-btn">
              {applying && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Apply Updates
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MassQuoteUpdate;
